import { t } from '@bia-auction/shared-i18n';
import { Badge, Checkbox, Input, SkeletonRows } from '@bia-auction/ui';
import { companySummarySchema } from '@bia-auction/shared-schema';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { api } from '../lib/api';
import { ErrorMsg } from './Feedback';

/**
 * Search-and-select multi-picker over the company directory
 * (`GET /admin/companies`) — replaces the raw comma-separated-UUID inputs on
 * the user form (`relatedCompany`) and the new-sale form (`targetCompany`).
 * Value stays a `string[]` of company ids, so callers are unchanged shape-wise.
 */
export function CompanyPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: () => api('/admin/companies', {}, z.array(companySummarySchema)),
    staleTime: 5 * 60_000, // small, slowly-changing directory
  });

  if (companies.isLoading) return <SkeletonRows rows={2} />;
  if (companies.isError) return <ErrorMsg error={companies.error} />;

  const byId = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
  const filter = search.trim().toLocaleLowerCase('tr');
  const visible = (companies.data ?? []).filter(
    (c) => !filter || c.name.toLocaleLowerCase('tr').includes(filter),
  );
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1">
              {byId.get(id) ?? id}
              <button
                type="button"
                aria-label={t('companyPicker.remove', { name: byId.get(id) ?? id })}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => toggle(id)}
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        placeholder={t('companyPicker.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
        {visible.map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-sm">
            <Checkbox checked={value.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
            {c.name}
          </label>
        ))}
        {visible.length === 0 && (
          <div className="py-2 text-center text-xs text-muted-foreground">
            {t('companyPicker.empty')}
          </div>
        )}
      </div>
    </div>
  );
}
